import { useState } from 'react';

import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import download from 'downloadjs';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

import { successToastMessage } from '../components/layout/toast-root/toast';

const fileMaxSize = 52428800;

const defaultDownloadSuccessMessageKey = 'messages.fileDownloadedDefault';

interface IDownloadFileOptions {
  mimeType?: string;
  successMessageKey?: string;
}

interface UseAppFileReturn {
  validateFile: (file: File) => string[];
  downloadFile: (
    storageLink: string,
    fileName: string,
    options?: IDownloadFileOptions,
  ) => Promise<void>;
  isDownloading: boolean;
}

export const useAppFile = (t: (key: string) => string): UseAppFileReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const validateFile = (file: File): string[] => {
    const messages: string[] = [];
    if (file.size > fileMaxSize) {
      messages.push(t('messages.fileOversized').replace('${1}', file.name));
    }
    return messages;
  };

  const downloadFile = async (
    storageLink: string,
    fileName: string,
    options?: IDownloadFileOptions,
  ): Promise<void> => {
    setIsDownloading(true);
    const successMessageKey =
      options?.successMessageKey ?? defaultDownloadSuccessMessageKey;
    try {
      const storage = getStorage();
      const fileRef = ref(storage, storageLink);
      const url = await getDownloadURL(fileRef);
      const isNative = Capacitor.getPlatform() !== 'web';
      const safeFileName = fileName.split('/').pop() || fileName;

      if (isNative) {
        await Filesystem.requestPermissions();
        const resolvedFileName = await resolveUniqueFileName(safeFileName);
        await Filesystem.downloadFile({
          url,
          path: resolvedFileName,
          directory: Directory.Documents,
        });

        successToastMessage(
          t(successMessageKey).replace('${1}', resolvedFileName),
        );
        return;
      }

      const response = await fetch(url);
      const blob = await response.blob();
      download(blob, safeFileName, options?.mimeType);
      successToastMessage(t(successMessageKey).replace('${1}', safeFileName));
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadFile, validateFile, isDownloading };
};

const resolveUniqueFileName = async (fileName: string): Promise<string> => {
  const { name, extension } = splitFileName(fileName);
  let candidate = fileName;

  for (let index = 1; index < 1000; index += 1) {
    try {
      await Filesystem.stat({
        path: candidate,
        directory: Directory.Documents,
      });
      candidate = `${name} (${index})${extension}`;
    } catch {
      return candidate;
    }
  }

  return candidate;
};

const splitFileName = (
  fileName: string,
): { name: string; extension: string } => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) {
    return { name: fileName, extension: '' };
  }

  return {
    name: fileName.slice(0, dotIndex),
    extension: fileName.slice(dotIndex),
  };
};
